import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE from '../api.js';

const C = {
  navy:    '#0f172a',
  ink:     '#1e293b',
  muted:   '#64748b',
  border:  '#e2e8f0',
  indigo:  '#4f46e5',
  indigoL: '#6366f1',
  indigoXL:'#eef2ff',
  snow:    '#f8fafc',
  white:   '#ffffff',
  green:   '#059669',
};

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: '11px 14px', borderRadius: 10,
  border: `1.5px solid ${C.border}`, fontSize: '0.9rem',
  color: C.navy, background: C.white, fontFamily: 'inherit',
  outline: 'none', transition: 'border-color 0.15s',
};

export default function Contact() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [form, setForm] = useState({ name: '', email: '', subject: 'support', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [focused, setFocused] = useState('');

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.error || 'Failed to send.');
      }
      setStatus('sent');
    } catch (err) {
      console.error('[contact]', err.message);
      setStatus('error');
    }
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: C.white, color: C.navy, minHeight: '100vh' }}>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}`, padding: '0 clamp(20px,5vw,64px)', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: C.navy }}>AssessMonk</span>
        </Link>
        <button onClick={() => navigate('/app')} style={{ padding: '7px 18px', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', background: C.indigo, color: '#fff', border: 'none', cursor: 'pointer' }}>
          Try Assessment
        </button>
      </nav>

      {/* Hero */}
      <div style={{ background: `linear-gradient(to bottom, ${C.indigoXL}, ${C.white})`, padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,64px) clamp(40px,6vw,64px)', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.indigoXL, border: '1px solid #c7d2fe', borderRadius: 99, padding: '5px 14px', fontSize: '0.7rem', fontWeight: 700, color: C.indigo, marginBottom: 20, letterSpacing: '0.03em' }}>
            Get in Touch
          </div>
          <h1 style={{ fontSize: 'clamp(1.9rem,4vw,2.8rem)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.03em', margin: '0 0 16px' }}>
            We're here to help.
          </h1>
          <p style={{ fontSize: '1rem', color: C.muted, lineHeight: 1.75, margin: '0 auto' }}>
            Whether you have a question about the platform, feedback for the team, or a business inquiry — reach out and we'll respond promptly.
          </p>
        </div>
      </div>

      {/* Main grid */}
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: 'clamp(40px,6vw,72px) clamp(20px,5vw,60px) 80px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, alignItems: 'start' }}>

        {/* Left: contact info */}
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: C.navy, margin: '0 0 6px', letterSpacing: '-0.02em' }}>Contact Information</h2>
          <p style={{ fontSize: '0.88rem', color: C.muted, lineHeight: 1.75, margin: '0 0 32px' }}>
            The AssessMonk team typically responds within 1–2 business days. For urgent issues, email is the fastest route.
          </p>

          {[
            {
              icon: '✉️',
              label: 'General Support',
              desc: 'Platform questions, login issues, assessment guidance.',
              value: 'assessmonk@gmail.com',
              href: 'mailto:assessmonk@gmail.com',
            },
            {
              icon: '💼',
              label: 'Business & Partnerships',
              desc: 'Institution licences, curriculum integration, research partnerships.',
              value: 'assessmonk@gmail.com',
              href: 'mailto:assessmonk@gmail.com',
            },
            {
              icon: '⚖️',
              label: 'Legal & Privacy',
              desc: 'Data requests, privacy questions, terms clarification.',
              value: 'assessmonk@gmail.com',
              href: 'mailto:assessmonk@gmail.com',
            },
          ].map(c => (
            <div key={c.label} style={{ display: 'flex', gap: 16, padding: '18px 0', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: C.indigoXL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                {c.icon}
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '0.88rem', color: C.navy, marginBottom: 3 }}>{c.label}</div>
                <div style={{ fontSize: '0.78rem', color: C.muted, lineHeight: 1.6, marginBottom: 5 }}>{c.desc}</div>
                <a href={c.href} style={{ fontSize: '0.82rem', fontWeight: 700, color: C.indigo, textDecoration: 'none' }}>{c.value}</a>
              </div>
            </div>
          ))}

          {/* Response time card */}
          <div style={{ marginTop: 28, background: C.indigoXL, borderRadius: 14, padding: '18px 20px', border: '1px solid #c7d2fe' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, display: 'inline-block' }} />
              <span style={{ fontSize: '0.7rem', fontWeight: 800, color: C.indigo, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Response times</span>
            </div>
            {[
              { type: 'Support queries', time: 'Within 1–2 business days' },
              { type: 'Bug reports', time: 'Within 1 business day' },
              { type: 'Business inquiries', time: 'Within 3 business days' },
              { type: 'Legal / data requests', time: 'Within 14 business days' },
            ].map(r => (
              <div key={r.type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #c7d2fe' }}>
                <span style={{ fontSize: '0.78rem', color: C.ink, fontWeight: 500 }}>{r.type}</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: C.indigo }}>{r.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: contact form */}
        <div style={{ background: C.white, borderRadius: 20, border: `1px solid ${C.border}`, padding: 'clamp(24px,4vw,36px)', boxShadow: '0 8px 32px rgba(15,23,42,0.07)' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: C.navy, margin: '0 0 6px', letterSpacing: '-0.02em' }}>Send a message</h2>
          <p style={{ fontSize: '0.82rem', color: C.muted, margin: '0 0 24px' }}>Fill in the form and we'll get back to you.</p>

          {status === 'sent' ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', margin: '0 auto 16px' }}>✓</div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: C.navy, marginBottom: 6 }}>Message sent!</div>
              <p style={{ fontSize: '0.85rem', color: C.muted, margin: 0 }}>We've received your message and will get back to you within 1–2 business days.</p>
              <button onClick={() => { setStatus('idle'); setForm({ name: '', email: '', subject: 'support', message: '' }); }} style={{ marginTop: 20, padding: '9px 20px', borderRadius: 9, border: 'none', background: C.indigoXL, color: C.indigo, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.ink, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name *</label>
                  <input
                    name="name" value={form.name} onChange={handleChange} required
                    placeholder="Your name"
                    style={{ ...inputStyle, borderColor: focused === 'name' ? C.indigo : C.border }}
                    onFocus={() => setFocused('name')} onBlur={() => setFocused('')}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.ink, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email *</label>
                  <input
                    name="email" type="email" value={form.email} onChange={handleChange} required
                    placeholder="your@email.com"
                    style={{ ...inputStyle, borderColor: focused === 'email' ? C.indigo : C.border }}
                    onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.ink, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</label>
                <select
                  name="subject" value={form.subject} onChange={handleChange}
                  style={{ ...inputStyle, appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 36, cursor: 'pointer', borderColor: focused === 'subject' ? C.indigo : C.border }}
                  onFocus={() => setFocused('subject')} onBlur={() => setFocused('')}
                >
                  <option value="support">Platform support</option>
                  <option value="bug">Bug report</option>
                  <option value="feedback">Feedback / feature request</option>
                  <option value="business">Business or partnership</option>
                  <option value="legal">Legal or privacy</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: C.ink, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Message *</label>
                <textarea
                  name="message" value={form.message} onChange={handleChange} required
                  placeholder="Describe your question, issue, or inquiry in as much detail as helpful..."
                  rows={6}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.65, borderColor: focused === 'message' ? C.indigo : C.border }}
                  onFocus={() => setFocused('message')} onBlur={() => setFocused('')}
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                style={{
                  padding: '13px', borderRadius: 11, border: 'none', cursor: status === 'sending' ? 'default' : 'pointer',
                  background: status === 'sending' ? '#c7d2fe' : `linear-gradient(135deg, ${C.indigo}, ${C.indigoL})`,
                  color: '#fff', fontWeight: 800, fontSize: '0.92rem',
                  boxShadow: status === 'sending' ? 'none' : `0 8px 20px ${C.indigo}33`,
                  transition: 'all 0.2s',
                }}
              >
                {status === 'sending' ? 'Sending…' : 'Send message →'}
              </button>

              <p style={{ fontSize: '0.72rem', color: '#94a3b8', textAlign: 'center', margin: 0 }}>
                You can also email us directly at{' '}
                <a href="mailto:assessmonk@gmail.com" style={{ color: C.indigo, fontWeight: 600 }}>assessmonk@gmail.com</a>
              </p>
            </form>
          )}
        </div>

      </div>

      {/* FAQ strip */}
      <div style={{ background: C.snow, borderTop: `1px solid ${C.border}`, padding: 'clamp(40px,6vw,64px) clamp(20px,5vw,60px)' }}>
        <div style={{ maxWidth: 840, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.1rem,2vw,1.4rem)', fontWeight: 800, color: C.navy, margin: '0 0 32px', textAlign: 'center', letterSpacing: '-0.02em' }}>Common questions</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {[
              { q: 'Is AssessMonk free to use?', a: 'Yes — core platform features are currently free during the early access period. Future premium tiers will be announced in advance.' },
              { q: 'Can I use it in clinical practice?', a: 'AssessMonk is an educational platform. AI outputs are for learning and reference — not to replace direct clinical assessment or professional judgment.' },
              { q: 'How do I delete my account?', a: 'Email assessmonk@gmail.com with your account email and we\'ll process full account deletion within 14 business days.' },
              { q: 'Is my data secure?', a: 'All data is transmitted over HTTPS, stored on secure infrastructure, and never sold to third parties. See our Privacy Policy for full details.' },
              { q: 'Can my institution use AssessMonk?', a: 'Yes — we support institution-wide access for physiotherapy programs. Contact us at assessmonk@gmail.com to discuss options.' },
              { q: 'What if the AI output seems wrong?', a: 'AI outputs are educational references, not clinical ground truth. Always apply clinical reasoning. Report anything unexpected via the contact form.' },
            ].map(({ q, a }) => (
              <div key={q} style={{ background: C.white, borderRadius: 14, padding: '18px 20px', border: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 800, fontSize: '0.88rem', color: C.navy, marginBottom: 8, lineHeight: 1.4 }}>{q}</div>
                <div style={{ fontSize: '0.8rem', color: C.muted, lineHeight: 1.7 }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: C.snow, padding: '24px clamp(20px,5vw,60px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 22, height: 22, borderRadius: 6, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: '0.82rem', color: C.navy }}>AssessMonk</span>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <Link to="/privacy" style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>
          <Link to="/terms" style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Terms</Link>
          <Link to="/contact" style={{ fontSize: '0.72rem', color: C.muted, textDecoration: 'none', fontWeight: 600 }}>Contact</Link>
        </div>
        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>© 2026 AssessMonk. All rights reserved.</div>
      </footer>

    </div>
  );
}
